import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	OnDestroy,
	OnInit,
	signal,
	WritableSignal
} from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { CheckedEvent } from 'lua-web-components/dist/types/shared/types/checkbox.model';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ValueLabel } from '../../builders/formly-builder.models';
import { collapseAnimation } from './collapse-animation';

/**
 * MultiCheckbox Component for formly
 *
 * We keep it on our side temp until ngx-formly switches to ivy
 *
 * https://github.com/ngx-formly/ngx-formly/issues/2532#issuecomment-832734418
 */
@Component({
	templateUrl: './lu-multicheckbox.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [collapseAnimation]
})
export class LuMulticheckboxComponent extends FieldType implements OnInit, OnDestroy {
	defaultOptions = {
		templateOptions: {
			options: [],
			formCheck: 'default', // 'default' | 'inline' | 'switch' | 'inline-switch',
			bulkSelectOptions: { enabled: false }
		}
	};

	bulkChecked = false;
	bulkIndeterminate = false;
	checkedCount = 0;
	totalCount = 0;
	bulkLabel = '';

	collapsedSignal: WritableSignal<boolean> = signal(false);
	iconSignal = computed(() => (this.collapsedSignal() ? 'lu-chevron-big-down' : 'lu-chevron-big-up'));

	private destroy$ = new Subject<void>();
	private optionsArray: ValueLabel[] = [];

	constructor(private cd: ChangeDetectorRef) {
		super();
	}

	ngOnInit(): void {
		this.collectSelectOptions();
		this.observeFormValueChanges();
		this.to.bulkSelectOptions.enabled && this.updateBulkStates();
		this.bulkLabel = this.to.bulkSelectOptions.label && `<span class="h-xs">${this.to.bulkSelectOptions.label}<span>`;
	}

	toggle(): void {
		this.collapsedSignal.update(value => !value);
	}

	onChange(value: any, checked: boolean): void {
		if (this.to.type === 'array') {
			this.formControl.patchValue(
				checked
					? [...(this.formControl.value || []), value]
					: [...(this.formControl.value || [])].filter(o => o !== value)
			);
		} else {
			this.formControl.markAsDirty();
			this.formControl.patchValue({ ...this.formControl.value, [value]: checked });
		}
	}

	isChecked(option: any): boolean {
		const value = this.formControl.value;

		return value && (this.to.type === 'array' ? value.indexOf(option.value) !== -1 : value[option.value]);
	}

	onBulkChecked(event: CustomEvent<CheckedEvent>): void {
		const checked = event.detail.checked;
		const optionValues = this.optionsArray.map(opt => opt.value);
		const newValue = this.getBulkCheckedValue(optionValues, checked);
		this.formControl.patchValue(newValue);
		this.cd.markForCheck();
	}

	private observeFormValueChanges(): void {
		this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(data => {
			this.to.bulkSelectOptions.enabled && this.updateBulkStates();
			if (data == null) {
				this.cd.markForCheck();
			}
		});
	}

	private collectSelectOptions(): void {
		const options$ = this.to.options;
		if (Array.isArray(options$)) {
			this.optionsArray = options$;
		} else if (options$ && typeof options$.subscribe === 'function') {
			(options$ as Observable<ValueLabel[]>).pipe(takeUntil(this.destroy$)).subscribe(arr => {
				this.optionsArray = arr || [];
				this.to.bulkSelectOptions.enabled && this.updateBulkStates();
			});
		}
	}

	private updateBulkStates(): void {
		const optionsArray = this.optionsArray;
		const value = Array.isArray(this.formControl.value)
			? this.formControl.value.filter(value => optionsArray.some(opt => opt.value === value))
			: this.formControl.value;
		this.checkedCount = 0;
		this.totalCount = optionsArray.length;

		this.to.type === 'array'
			? (this.checkedCount = Array.isArray(value) ? value.length : 0)
			: (this.checkedCount = optionsArray.filter(opt => value && value[opt.value]).length);

		this.bulkChecked = this.checkedCount === this.totalCount && this.totalCount > 0;
		this.bulkIndeterminate = this.checkedCount > 0 && this.checkedCount < this.totalCount;
	}

	private getBulkCheckedValue(optionValues: any[], checked: boolean): unknown[] {
		let newValue = [];
		if (this.to.type === 'array') {
			const current = Array.isArray(this.formControl.value) ? this.formControl.value : [];
			newValue = checked
				? Array.from(new Set([...current, ...optionValues]))
				: current.filter(value => !optionValues.includes(value));
		} else {
			const current = this.formControl.value || {};
			newValue = { ...current };
			optionValues.forEach(value => {
				newValue[value] = checked;
			});
		}
		return newValue;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
